import Button from 'react-bootstrap/Button'
import React from "react";
import { ClaimModel } from "../../../../models/ClaimModel";
import { RepresentationEntry } from "../../../../models/RepresentationEntry";
import { DateField, MoneyField, MultiLineField, NumericSelectField, TextField, TextMultiSelectField, validateFields } from "../../../../shared/fields";
import { Option } from "../../../../shared/fields/Options";
import { PercentageField } from "../../../../shared/fields/PercentageField";
import { ShowIf } from "../../../../shared/fields/ShowIf";
import { ISection, TableColumn } from "../../../../shared/table";
import { formatNumber, grantPeriodEnd, grantPeriodStart, newGuid, paymentMethodDataSource, customRound, getMarketString, formatPaymentMethodForDisplay, formatDateForDisplay, formatMoneyForDisplay, formatPercentageForDisplay, sortSchedules } from "../../../../shared/Utility";

export const representationEntryColumns = (markets: Option<string>[]): TableColumn<RepresentationEntry>[] => [
    { name: "Your Ref No.", content: d => d.reference },
    { name: "Expense item", content: d => d.name },
    { name: "Country", content: d => { return getMarketString(d.markets, markets); }, responsive: "md" },
    { name: "Supporting evidence", content: d => d.evidence, printOnly: true },
    { name: "Date of payment", content: d => formatDateForDisplay(d.date) },
    { name: "Payment method", content: d => formatPaymentMethodForDisplay(d.paymentMethod), printOnly: true },
    { name: "Total retainer $A", content: d => formatMoneyForDisplay(d.retainerAmount), printOnly: true },
    { name: "Total retainer claimed %", content: d => formatPercentageForDisplay(d.retainerPercentage), printOnly: true },
    { name: "Claimed retainer $A", content: d => formatMoneyForDisplay(calculateRepresentationRetainer(d)) },
    { name: "Total expenses $A", content: d => formatMoneyForDisplay(d.expenseAmount), printOnly: true },
    { name: "Total expenses claimed %", content: d => formatPercentageForDisplay(d.expensePercentage), printOnly: true },
    { name: "Claimed Expenses $A", content: d => formatMoneyForDisplay(calculateRepresentationExpense(d)) },
    { name: "Notes", content: d => d.notes, printOnly: true }
];

export const calculateRepresentationRetainer = (d: RepresentationEntry): number => {
    return customRound((d.retainerAmount || 0) * (d.retainerPercentage || 0));
}

export const calculateRepresentationExpense = (d: RepresentationEntry): number => {
    return customRound((d.expenseAmount || 0) * (d.expensePercentage || 0));
}

export const calculateRepresentationAmount = (d: RepresentationEntry): number => {
    return calculateRepresentationRetainer(d) + calculateRepresentationExpense(d);
}

export const calculateRepresentationTotal = (entries: RepresentationEntry[]): number => {
    let total = 0;
    for (let e of entries) {
        total += calculateRepresentationAmount(e);
    }
    return total;
}

export const sortRepresentationEntries = (a: RepresentationEntry, b: RepresentationEntry): number => {
    return sortSchedules(a.invalid, b.invalid, a.reference, b.reference);
}

export const sortRepresentationByAmountDesc = (a: RepresentationEntry, b: RepresentationEntry): number => {
    return calculateRepresentationAmount(b) - calculateRepresentationAmount(a);
}

export interface RepresentationEntryDetailsProps {
    id: string
    model: RepresentationEntry
    claim: ClaimModel
    markets: Option<string>[]
    onChange: { (v: RepresentationEntry, close: boolean): any }
    onClose: { (): any }
    onRemove: { (): any }
    importing: boolean
    preview?: boolean
}

export interface RepresentationEntryDetailsState {
    model: RepresentationEntry
    validated: boolean
}

export class RepresentationEntryDetails extends React.Component<RepresentationEntryDetailsProps, RepresentationEntryDetailsState> implements ISection {
    reference: TextField
    name: TextField
    markets: TextMultiSelectField
    evidence: TextField
    date: DateField
    paymentMethod: NumericSelectField 
    retainerAmount: MoneyField
    retainerPercentage: PercentageField
    expenseAmount: MoneyField
    expensePercentage: PercentageField
    notes: MultiLineField

    constructor(props: RepresentationEntryDetailsProps) {
        super(props);

        let model: RepresentationEntry = this.props.model ? { ...this.props.model } : {
            id: newGuid(),
            reference: '',
            name: '',
            date: null,
            evidence: '',
            paymentMethod: null,
            expenseAmount: null,
            expensePercentage: null,
            retainerAmount: null,
            retainerPercentage: null,
            markets: [],
            invalid: false,
            notes: ''
        };

        if (model.markets == null)
            model.markets = [];

        this.state = {
            model,
            validated: false
        };
    }

    validate = (callback: { (res: boolean): any }) => {
        this.setState({ validated: true }, () => {
            validateFields(res => this.onChange({ invalid: !res }, () => callback(res)),
                this.reference,
                this.name,
                this.markets,
                this.evidence,
                this.date,
                this.paymentMethod,
                this.retainerAmount,
                this.retainerPercentage,
                this.expenseAmount,
                this.expensePercentage,
                this.notes
            );
        });
    }

    saveAndClose = (callback?: { (res: boolean): any }) => {
        this.validate(res => {
            if (res) {
                this.props.onChange(this.state.model, true);
            }
            callback && callback(res);
        });
    }

    componentDidMount() {
        if (this.props.importing) {
            this.validate(res => {
                var temp = { invalid: !res }
                this.props.onChange({ ...this.state.model, ...temp }, true);
            })
        }
        else {
            if (this.state.model.invalid) {
                this.validate(() => { });
            }
        }
    }

    onChange = (change: Partial<RepresentationEntry>, callback?: {():any}) => {
        this.setState({
            model: { ...this.state.model, ...change }
        }, callback);
    }

    validateExpense(): string {
        if (this.state.validated && !this.state.model.expenseAmount && !this.state.model.retainerAmount)
            return "Please complete at least one of 'Total retainer A$' and 'Total retainer claimed %' or 'Total expenses A$' and 'Total expenses claimed %";

        return "";
    }

    render() {
        let model = this.state.model;

        return <>
            <TextField ref={r => this.reference = r}
                id={`${this.props.id}-reference`}
                label="Your Ref No."
                value={model.reference}
                onChange={reference => this.onChange({ reference })}
                block={false}
                maxLength={10}
                preview={this.props.preview}
                className="mt-0"
            />
            <TextField ref={r => this.name = r}
                id={`${this.props.id}-name`}
                label="Expense item"
                value={model.name}
                onChange={name => this.onChange({ name })}
                required={true}
                maxLength={80}
                preview={this.props.preview}
            />
            <TextMultiSelectField ref={r => this.markets = r}
                id={`${this.props.id}-markets`}
                label="Country"
                value={model.markets}
                onChange={markets => this.onChange({ markets })}
                dataSource={this.props.markets}
                required={true}
                preview={this.props.preview}
            />
            <TextField ref={r => this.evidence = r}
                id={`${this.props.id}-evidence`}
                label="Supporting evidence"
                value={model.evidence}
                onChange={evidence => this.onChange({ evidence })}
                required={true}
                maxLength={80}
                preview={this.props.preview}
            />
            <DateField ref={r => this.date = r}
                id={`${this.props.id}-date`}
                label="Date of payment"
                value={model.date}
                onChange={date => this.onChange({ date })}
                required={true}
                min={grantPeriodStart(this.props.claim.claimYear, this.props.claim.claimTwoYears)}
                max={grantPeriodEnd(this.props.claim.claimYear)}
                preview={this.props.preview}
            />
            <NumericSelectField ref={r => this.paymentMethod = r}
                id={`${this.props.id}-paymentMethod`}
                label="Payment method"
                value={model.paymentMethod}
                onChange={paymentMethod => this.onChange({ paymentMethod })}
                dataSource={paymentMethodDataSource}
                required={true}
                block={false}
                preview={this.props.preview}
            />
            <div className="clearfix">
                <MoneyField ref={r => this.retainerAmount = r}
                    className="float-left mr-3"
                    id={`${this.props.id}-retainerAmount`}
                    label="Total retainer $A"
                    value={model.retainerAmount}
                    onChange={retainerAmount => this.onChange({ retainerAmount })}
                    preview={this.props.preview}
                    errorMessage={this.validateExpense()}
                />
                <PercentageField ref={r => this.retainerPercentage = r}
                    className="float-left mr-3"
                    id={`${this.props.id}-retainerPercentage`}
                    label="Total retainer claimed %"
                    value={model.retainerPercentage}
                    onChange={retainerPercentage => this.onChange({ retainerPercentage })}
                    required={model.retainerAmount ? true : false}
                    preview={this.props.preview}
                />
                <MoneyField
                    className="float-left"
                    id={`${this.props.id}-retainerClaimed`}
                    label="Claimed retainer"
                    value={calculateRepresentationRetainer(model)}
                    onChange={retainerAmount => { }}
                    preview={this.props.preview}
                    disabled={true}
                />
            </div>
            <div className="clearfix">
                <MoneyField ref={r => this.expenseAmount = r}
                    className="float-left mr-3"
                    id={`${this.props.id}-expenseAmount`}
                    label="Total expenses $A"
                    value={model.expenseAmount}
                    onChange={expenseAmount => this.onChange({ expenseAmount })}
                    preview={this.props.preview}
                    errorMessage={this.validateExpense()}
                />
                <PercentageField ref={r => this.expensePercentage = r}
                    className="float-left mr-3"
                    id={`${this.props.id}-expensePercentage`}
                    label="Total expenses claimed %"
                    value={model.expensePercentage}
                    onChange={expensePercentage => this.onChange({ expensePercentage })}
                    required={model.expenseAmount ? true : false}
                    preview={this.props.preview}
                />
                <MoneyField
                    className="float-left"
                    id={`${this.props.id}-expenseClaimed`}
                    label="Claimed expense"
                    value={calculateRepresentationExpense(model)}
                    onChange={expenseAmount => { }}
                    preview={this.props.preview}
                    disabled={true}
                />
            </div>
            <MultiLineField ref={r => this.notes = r}
                id={`${this.props.id}-notes`}
                label="Notes"
                value={model.notes}
                onChange={notes => this.onChange({ notes })}
                required={false}
                maxLength={4000}
                preview={this.props.preview}
            />
            <ShowIf condition={!this.props.preview} className="clearfix mt-3">
                <Button id={`${this.props.id}-btnSave`} className="emdg-btn float-right" onClick={e => this.saveAndClose()}>Complete row</Button>
                <Button id={`${this.props.id}-btnCancel`} className="emdg-btn emdg-btn--secondary float-left mr-2" onClick={this.props.onClose} >Cancel</Button>
                <Button id={`${this.props.id}-btnRemove`} className="emdg-btn emdg-btn--secondary float-left" onClick={this.props.onRemove} style={{ display: this.props.model ? "" : "none" }}>Remove</Button>
            </ShowIf>
        </>
    }
}